import { css } from "@emotion/react";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import {
	AppWindowIcon,
	CheckIcon,
	LanguagesIcon,
	LogInIcon,
	LogOutIcon,
	MoonIcon,
	ShoppingCartIcon,
	SquareAsteriskIcon,
	SunIcon,
	UserIcon,
	UserPlusIcon,
	UserXIcon,
	WalletIcon,
	XIcon,
} from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AppConstants } from "../data/AppConstants";
import { useAppConfigState } from "../hooks/useAppConfigState";
import { useCatalogState } from "../hooks/useCatalogState";
import { useCheckoutFormState } from "../hooks/useCheckoutFormState";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useInventoryState } from "../hooks/useInventoryState";
import { useLocaleState } from "../hooks/useLocaleState";
import { useLoginFormState } from "../hooks/useLoginFormState";
import { useRegisterFormState } from "../hooks/useRegisterFormState";
import { useSessionState } from "../hooks/useSessionState";
import { useShoppingCartItemsState } from "../hooks/useShoppingCartItemsState";
import { useSiteLayoutState } from "../hooks/useSiteLayoutState";
import { useStorefrontSettingsState } from "../hooks/useStorefrontSettingsState";
import { useTailwindMediaQueries } from "../hooks/useTailwindMediaQueries";
import { useThemeState } from "../hooks/useThemeState";
import { useTranslate } from "../hooks/useTranslate/useTranslate";
import { CompleteResetPasswordForm } from "./CompleteResetPasswordForm";
import { RegisterForm } from "./RegisterForm";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

const lucideIconButtonSize: number = 28;
const lucideIconMenuSize: number = 22;
const topRightIconDefaultClassName = "size-5.5";
const topRightIconDesktopClassName = "size-4";
const topRightIconButtonDefaultClassName = "size-10";
const topRightIconButtonDesktopClassName = "size-8";
type SiteLayoutProps = {
	title?: string;
	companyName?: string;
	fullLeftGutter?: React.ReactNode;
	mainLeftGutter?: React.ReactNode;
	mainHeader?: React.ReactNode;
	mainChildren?: React.ReactNode;
	mainFooter?: React.ReactNode;
	mainRightGutter?: React.ReactNode;
	rightGutter?: React.ReactNode;
	afterLogo?: React.ReactNode;
	topRightMenu?: React.ReactNode;
};
export const SiteLayout: React.FC<SiteLayoutProps> = (props) => {
	const navigate = useNavigate();
	const themeState = useThemeState();
	//const themeName = themeState.get().themeName;
	const sessionState = useSessionState();
	const siteLayoutState = useSiteLayoutState();
	const loginFormState = useLoginFormState();
	const contactRegistrationFormState = useRegisterFormState();
	useDocumentTitle(props.title ?? "");
	const shoppingCartItemsState = useShoppingCartItemsState();
	const appConfigState = useAppConfigState();
	const appConfig = appConfigState.get().appConfig;
	//let shoppingCartCount = shoppingCartItemsState.get().length;
	let shoppingCartCount = 0;
	const items = shoppingCartItemsState.get();
	for (let i = 0; i < items.length; i++) {
		shoppingCartCount += items[i].qty;
	}
	const catalogState = useCatalogState();
	//const isDesktop = useIsDesktopMediaQuery();
	const mediaQueries = useTailwindMediaQueries();
	const topRightLucideIconClassName = mediaQueries.desktop
		? topRightIconDesktopClassName
		: topRightIconDefaultClassName;
	const topRightIconButtonClassName = mediaQueries.desktop
		? topRightIconButtonDesktopClassName
		: topRightIconButtonDefaultClassName;
	const inventoryState = useInventoryState();
	const checkoutFormState = useCheckoutFormState();
	const fromDateValue = checkoutFormState.get().fromDateValue;
	const toDateValue = checkoutFormState.get().toDateValue;
	const todayStr = useMemo(
		() => dayjs().startOf("day").format("YYYY-MM-DD"),
		[],
	);

	const storefrontSettingsState = useStorefrontSettingsState();
	let homepageShowSiteHeader = true;
	if (
		window.location.pathname === "/storefront" ||
		window.location.pathname === "/storefront/"
	) {
		homepageShowSiteHeader =
			storefrontSettingsState.get().settings?.HomepageShowSiteHeader ?? true;
	}
	const isAnonymousAccessEnabled =
		storefrontSettingsState.get().settings?.IsAnonymousAccessEnabled ?? false;
	const isUniversity =
		storefrontSettingsState.get().settings?.IsUniversity ?? false;
	const filterCatalogsByLocation =
		storefrontSettingsState.get().settings?.FilterCatalogsByLocation ?? false;
	const isInventoryPage = window.location.pathname.startsWith(
		"/storefront/inventory",
	);
	const isShoppingCartPage = window.location.pathname.startsWith(
		"/storefront/shoppingcart",
	);
	const isCheckoutPage = window.location.pathname.startsWith(
		"/storefront/checkout",
	);
	const isProductPage = window.location.pathname.startsWith(
		"/storefront/product",
	);
	const loggedIn = sessionState.get().loggedIn;
	const logggedIn = sessionState.get().loggedIn;
	const shoppingCartEnabled = isAnonymousAccessEnabled || logggedIn;
	useEffect(() => {
		if (!loginFormState.get().isPopup) {
			loginFormState.setIsPopup(true);
		}

		// update dates on checkoutFormState
		let fromDate = fromDateValue;
		let toDate = toDateValue;

		if (dayjs(fromDate).isBefore(todayStr)) {
			fromDate = todayStr;
			toDate = dayjs(fromDate).add(6, "day").format("YYYY-MM-DD");
		}
		if (dayjs(toDate).isBefore(fromDate)) {
			toDate = dayjs(fromDate).add(6, "day").format("YYYY-MM-DD");
		}
		if (fromDate !== fromDateValue || toDate !== toDateValue) {
			checkoutFormState.setDates(fromDate, toDate);
		}
	}, [checkoutFormState, fromDateValue, loginFormState, toDateValue, todayStr]);

	useEffect(() => {
		if (loggedIn) {
			//console.log('refresh catalogs');
			catalogState.loadAsync(true);
		}
	}, [loggedIn, catalogState.loadAsync]);

	let catalogs = catalogState.get().catalogs || [];
	const hasLocationSelector =
		isInventoryPage || isShoppingCartPage || isCheckoutPage || isProductPage;
	if (filterCatalogsByLocation && !isUniversity && hasLocationSelector) {
		//catalogs = catalogs.filter(c => c.WarehouseId === checkoutFormState.getWarehouse()?.WarehouseId);
		catalogs = catalogState.getWarehouseCatalogs(
			checkoutFormState.getWarehouse()?.WarehouseId,
		);
	}
	//const inventory = inventoryState.get();
	//console.log({ catalogs, inventory })

	useEffect(() => {
		if (isShoppingCartPage) {
			return;
		}
		inventoryState.updateSelectedCatalog();
	}, [isShoppingCartPage, inventoryState.updateSelectedCatalog]);

	const catalogLinks = catalogs.map((catalog) => {
		const isSelected =
			inventoryState.get().catalog &&
			catalog.Id === (inventoryState.get().catalog?.Id ?? "");

		return (
			<li key={catalog.Id}>
				<Link
					to={`/inventory/${catalog.Id}/index`}
					css={css`text-decoration:none;`}
					onClick={() => {
						checkoutFormState.selectCatalogLocation(catalog);
					}}
				>
					<div
						css={css`
                            flex:1 1 0;
                            font-size:.8rem;
                            white-space:no-wrap;
                            padding:0 2em 0 0;
                            color: ${isSelected ? "var(--catalog-selected)" : "var(--foreground)"};
                            transition: color .1s;
                            &:hover {
                                color: var(--catalog-selected);
                            }
                            `}
					>
						{catalog.Name}
					</div>
				</Link>
			</li>
		);
	});

	return (
		<>
			<div
				css={css`
                position:fixed;
                top:0;
                right:0;
                bottom:0;
                left:0;
                display:flex;
                overflow:hidden;
                background-color: ${themeState.get().theme.palette.siteBackground};
                color:${themeState.get().theme.palette.siteTextPrimary};
                transition: all 1s;`}
			>
				<motion.div
					css={css`flex:0 0 auto;display:flex;flex-direction:column;overflow:hidden;`}
					layout
				>
					{props.fullLeftGutter}
				</motion.div>
				<motion.div
					css={css`flex:1 1 0;min-height:0;display:flex;flex-direction:column;`}
					layout
				>
					{homepageShowSiteHeader && (
						<header
							className="border-b border-b-gray-200 dark:border-b-background transition-all"
							css={css`
							flex: 0 1 auto;
							display: flex;
							flex-direction: column;
							justify-content: start;
							background-color: var(--header-background-color);
							transition: background-color 1s;`}
						>
							<div
								css={css`
								flex:0 0 auto;
								display:flex;
								align-items: center;`}
							>
								<div
									css={css`
									flex: 1 1 0;
									display:flex;
									margin:0 auto;
									max-width:${AppConstants.siteWidthCss};`}
								>
									{/* Company Logo */}
									{appConfig && (
										<div
											css={css`
											flex: 0 0 auto;
											display: flex;
											align-items:center;`}
										>
											<div
												css={css`
											margin:4px 8px;
											display:flex;
											align-items:center;
											img {
												max-height:64px;
												max-width:128px;
											}
											`}
											>
												<Link to="/">
													<img
														src={appConfig.CompanyLogoUrl}
														alt={appConfig.CompanyName}
														onClick={async () => {
															return Promise.resolve().then(() => {
																inventoryState.clearCatalogFilterAsync();
															});
														}}
													/>
												</Link>
												{props.afterLogo}
											</div>
										</div>
									)}

									<div
										css={css`
									flex: 1 1 0;
									@media print {
										& {
											display: none;
										}
								`}
									>
										<div
											css={css`
												flex: 1 1 0;
												display: flex;
												align-items: center;
												justify-content: flex-end;
												margin: 16px 0 0 0;`}
										>
											{mediaQueries.minw_md && (
												<ul
													css={css`
														flex:1 1 0;
														padding: 0;
														display: flex;
														flex-wrap: wrap;
														align-items: center;
														justify-content: flex-end;
														font-weight: var(--font-weight-medium);
														`}
												>
													{catalogLinks}
												</ul>
											)}

											<div
												css={css`flex: 0 0 auto;display:flex;align-items:center;gap:.5rem;padding-right:.5rem;`}
											>
												{props.topRightMenu}

												{shoppingCartEnabled && (
													<Tooltip>
														<TooltipTrigger
															render={
																<Button
																	className={`${topRightIconButtonClassName} relative`}
																	variant="outline"
																	size="icon"
																	aria-label="shopping cart"
																	onClick={() => navigate("/shoppingcart")}
																>
																	<ShoppingCartIcon
																		className={topRightLucideIconClassName}
																	/>
																	{shoppingCartCount > 0 && (
																		<Badge className="absolute -right-2 -top-2 h-5 min-w-5 justify-center rounded-full bg-[oklch(0.62_0.26_144.9)] px-1 text-[0.65rem] text-white pointer-events-none">
																			{shoppingCartCount > 99999
																				? "99999+"
																				: shoppingCartCount}
																		</Badge>
																	)}
																</Button>
															}
														/>
														<TooltipContent>
															{siteLayoutState.translate("Shopping Cart")}
														</TooltipContent>
													</Tooltip>
												)}
												{!shoppingCartEnabled && (
													<Tooltip>
														<TooltipTrigger
															render={
																<Button
																	className={topRightIconButtonClassName}
																	variant="outline"
																	size="icon"
																	aria-label={siteLayoutState.translate(
																		"Shopping Cart",
																	)}
																	disabled
																>
																	<ShoppingCartIcon
																		className={topRightLucideIconClassName}
																	/>
																</Button>
															}
														/>
														<TooltipContent>
															{siteLayoutState.translate("Shopping Cart")}
														</TooltipContent>
													</Tooltip>
												)}
												{(isAnonymousAccessEnabled || logggedIn) && (
													<AccountMenu
														topRightIconButtonClassName={
															topRightIconButtonClassName
														}
														topRightLucideIconClassName={
															topRightLucideIconClassName
														}
													/>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</header>
					)}

					<main
						css={css`
                        flex: 1 1 0;
                        min-height: 0;
                        display: flex;
                        overflow: hidden;
                        max-width:100vw;`}
					>
						<div
							css={css`
                            flex: 0 0 auto;
                            display: flex;
                            flex-direction: column;
                            overflow: auto;`}
						>
							{props.mainLeftGutter}
						</div>
						<div
							css={css`flex:1 1 0;min-height:0;display:flex;flex-direction:column;overflow:auto;`}
						>
							<div
								css={css`flex:0 0 auto;display:flex;justify-content:flex-start;`}
							>
								{props.mainHeader}
							</div>
							<div
								css={css`
                            flex:1 1 0;
                            min-height:0;
                            display:flex;
                            flex-direction:column;
                            justify-content:flex-start;
                            @media screen {
                                overflow:hidden;
                            }
                            `}
							>
								{props.mainChildren}
							</div>
							<div css={css`flex:0 0 auto;display:flex;flex-direction:column;`}>
								{props.mainFooter}
							</div>
							<div css={css`flex:0 0 auto;display:flex;`}>
								<div css={css`display:flex;font-weight:bold`}>
									{/* <FooterLink name="Privacy" href="/privacy" />
                                <FooterLink name="About" href="/about" /> */}
								</div>
							</div>
						</div>
						<div
							css={css`flex:0 0 auto;display:flex;flex-direction:column;overflow:auto;`}
						>
							{props.mainRightGutter}
						</div>
					</main>
					<footer
						css={css`
                        display:flex:0 0 auto;
                        display:flex;
                        justify-content:center;
                        background-color: #333333;
                        padding-left: 44px;
                        @media (min-width: 768px) {
                            padding-left: 0;
                        }`}
					></footer>
				</motion.div>
				<motion.div
					css={css`flex:0 0 auto;display:flex;flex-direction:column;`}
					layout
				>
					{props.rightGutter}
				</motion.div>
			</div>
			<AnimatePresence>
				{siteLayoutState.get().resetPasswordFormPopupVisible && (
					<div
						css={css`
							background-color:rgba(0,0,0,.5);
							position:fixed;
							top:0;
							left:0;
							right:0;
							bottom:0;
							z-index:1;
							display:flex;
							align-items:center;
							justify-content:center;`}
					>
						<motion.div
							css={css`
								margin: 1em;
								padding: 1em;
								z-index: 2;
								background-color: ${themeState.get().theme.palette.siteBackground};
								overflow:hidden;
								box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
								position: relative;`}
							layout
							layoutId="loginform"
							transition={{ type: "spring", stiffness: 300, damping: 22 }}
							onClick={async (e) => {
								e.stopPropagation();
							}}
						>
							<Button
								variant="ghost"
								size="icon"
								css={css`position:absolute;top:8px;right:8px;`}
								onClick={() => {
									return Promise.resolve().then(() => {
										siteLayoutState.setResetPasswordFormPopupVisible(false);
									});
								}}
							>
								<XIcon size={lucideIconButtonSize} />
							</Button>
							<CompleteResetPasswordForm type="completeresetpassword" />
						</motion.div>
					</div>
				)}

				{siteLayoutState.get().registerFormPopupVisible && (
					<div
						css={css`
							background-color:rgba(0,0,0,.5);
							position:fixed;
							top:0;
							left:0;
							right:0;
							bottom:0;
							z-index:1;
							display:flex;
							align-items:center;
							justify-content:center;`}
					>
						<motion.div
							css={css`
								margin: 1em;
								padding: 1em;
								z-index: 2;
								background-color: ${themeState.get().theme.palette.siteBackground};
								overflow:hidden;
								box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
								position: relative;
								flex: 1 1 0;
								max-width: 400px;
                        `}
							layout
							layoutId="registerform"
							transition={{ type: "spring", stiffness: 300, damping: 22 }}
							onClick={async (e) => {
								e.stopPropagation();
							}}
						>
							<Button
								variant="ghost"
								size="icon"
								css={css`position:absolute;top:8px;right:8px;`}
								onClick={() => {
									return Promise.resolve().then(() => {
										siteLayoutState.setRegisterFormPopupVisible(false);
									});
								}}
							>
								<XIcon size={lucideIconButtonSize} />
							</Button>
							<RegisterForm
								registerFormState={contactRegistrationFormState}
							></RegisterForm>
						</motion.div>
					</div>
				)}
			</AnimatePresence>
		</>
	);
};

type AccountMenuProps = {
	topRightIconButtonClassName: string;
	topRightLucideIconClassName: string;
};
const AccountMenu: React.FC<AccountMenuProps> = ({
	topRightIconButtonClassName,
	topRightLucideIconClassName,
}) => {
	const translate = useTranslate();
	const sessionState = useSessionState();
	const siteLayoutState = useSiteLayoutState();
	const navigate = useNavigate();
	const location = useLocation();
	const redirectTarget = `${location.pathname}${location.search}`;

	const [openAccountMenu, setOpenAccountMenu] = useState(false);
	// const triggerSizePxBySize = {
	// 	small: 32,
	// 	medium: 40,
	// 	large: 48,
	// } as const;
	// const triggerSizePx = triggerSizePxBySize[props.iconButtonSize ?? "large"];
	const closeAccountMenu = (language?: string) => {
		setOpenAccountMenu(false);
		if (language) {
			localeState.setLanguage(language);
		}
	};

	const resetPasswordType: "change" | "reset" = sessionState.get().loggedIn
		? "change"
		: "reset";
	const resetPasswordText = sessionState.get().loggedIn
		? siteLayoutState.translate("Change Password")
		: siteLayoutState.translate("Forgot Password");
	const version = APP_VERSION ?? "0.0.0";
	const year = dayjs().year();
	const themeState = useThemeState();

	const localeState = useLocaleState();
	const currentLanguage = localeState.get().language;
	const storefrontSettingsState = useStorefrontSettingsState();
	const registerEnabled =
		storefrontSettingsState.get().settings?.RegisterEnabled ?? false;
	const isUniversity =
		storefrontSettingsState.get().settings?.IsUniversity ?? false;

	const appConfigState = useAppConfigState();
	const hasPortal = !!appConfigState.get().appConfig?.PortalAppUrl;

	return (
		<DropdownMenu
			open={openAccountMenu}
			onOpenChange={(nextOpen) => {
				setOpenAccountMenu(nextOpen);
			}}
		>
			<DropdownMenuTrigger
				aria-label="account"
				id="account-button"
				render={
					<Button
						className={topRightIconButtonClassName}
						variant="outline"
						size="icon"
						aria-label={siteLayoutState.translate("User Account Menu")}
						title={siteLayoutState.translate("User Account Menu")}
					>
						{sessionState.get().loggedIn ? (
							<UserIcon className={topRightLucideIconClassName} />
						) : (
							<UserXIcon className={topRightLucideIconClassName} />
						)}
					</Button>
				}
			/>
			<DropdownMenuContent align="end" className="min-w-56">
				{hasPortal && !isUniversity && (
					<DropdownMenuGroup>
						<DropdownMenuLabel>Portal</DropdownMenuLabel>

						<DropdownMenuItem
							onClick={async () => {
								return Promise.resolve().then(() => {
									window.location.href = "/portal";
								});
							}}
						>
							<WalletIcon size={lucideIconMenuSize} />
							Customer Portal
						</DropdownMenuItem>
						<DropdownMenuSeparator />
					</DropdownMenuGroup>
				)}

				<DropdownMenuGroup>
					<DropdownMenuLabel>Store</DropdownMenuLabel>

					<DropdownMenuItem
						disabled={!sessionState.get().loggedIn}
						onClick={async () => {
							return Promise.resolve().then(() => {
								navigate("/orders");
							});
						}}
					>
						<AppWindowIcon size={lucideIconMenuSize} />
						{isUniversity
							? translate("Equipment Requests")
							: translate("Orders")}
					</DropdownMenuItem>
				</DropdownMenuGroup>
				<DropdownMenuSeparator />
				<DropdownMenuItem
					onClick={async () => {
						return Promise.resolve().then(() => {
							closeAccountMenu();
							if (themeState.isDark()) {
								themeState.set("light");
							} else {
								themeState.set("dark");
							}
						});
					}}
				>
					{!themeState.isDark() && <MoonIcon size={lucideIconMenuSize} />}
					{themeState.isDark() && <SunIcon size={lucideIconMenuSize} />}
					{!themeState.isDark()
						? siteLayoutState.translate("Dark Theme")
						: siteLayoutState.translate("Light Theme")}
				</DropdownMenuItem>
				<DropdownMenuSeparator />
				<DropdownMenuSub>
					<DropdownMenuSubTrigger>
						<span className="inline-flex w-5 justify-center">
							{currentLanguage && <LanguagesIcon size={lucideIconMenuSize} />}
						</span>
						{translate("Language")}
					</DropdownMenuSubTrigger>
					<DropdownMenuSubContent>
						<DropdownMenuItem
							onClick={async () => {
								return Promise.resolve().then(() => {
									closeAccountMenu("en");
								});
							}}
						>
							<span className="inline-flex w-5 justify-center">
								{currentLanguage === "en" && (
									<CheckIcon size={lucideIconMenuSize} />
								)}
							</span>
							English
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={async () => {
								return Promise.resolve().then(() => {
									closeAccountMenu("es");
								});
							}}
						>
							<span className="inline-flex w-5 justify-center">
								{currentLanguage === "es" && (
									<CheckIcon size={lucideIconMenuSize} />
								)}
							</span>
							Español
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={async () => {
								return Promise.resolve().then(() => {
									closeAccountMenu("fr");
								});
							}}
						>
							<span className="inline-flex w-5 justify-center">
								{currentLanguage === "fr" && (
									<CheckIcon size={lucideIconMenuSize} />
								)}
							</span>
							Français
						</DropdownMenuItem>
					</DropdownMenuSubContent>
				</DropdownMenuSub>
				<DropdownMenuSeparator />
				{/* <DropdownMenuItem disabled className="block cursor-default">
					<div
						css={css`
							padding: 0.1rem 0;
							font-size: 0.72rem;
							color: var(--muted-foreground);
							word-break: break-word;
						`}
					>
						{sessionState.get().email ||
							siteLayoutState.translate("Not logged in")}
					</div>
				</DropdownMenuItem> */}

				{!sessionState.get().loggedIn && (
					<DropdownMenuItem
						disabled={sessionState.get().loggedIn}
						onClick={async () => {
							return Promise.resolve().then(() => {
								closeAccountMenu();
								navigate(
									`/login?redirect=${encodeURIComponent(redirectTarget)}`,
								);
							});
						}}
					>
						<LogInIcon size={lucideIconMenuSize} />
						{siteLayoutState.translate("Login")}
					</DropdownMenuItem>
				)}

				{!sessionState.get().loggedIn && registerEnabled && (
					<DropdownMenuItem
						disabled={sessionState.get().loggedIn}
						onClick={async () => {
							return Promise.resolve().then(() => {
								closeAccountMenu();
								navigate(
									`/register?redirect=${encodeURIComponent(redirectTarget)}`,
								);
							});
						}}
					>
						<UserPlusIcon size={lucideIconMenuSize} />
						{siteLayoutState.translate("Register")}
					</DropdownMenuItem>
				)}
				<DropdownMenuItem
					onClick={async () => {
						return Promise.resolve().then(() => {
							closeAccountMenu();
							if (sessionState.get().loggedIn) {
								siteLayoutState.sendResetPasswordEmailAsync(resetPasswordType);
							} else {
								navigate("/reset-password");
							}
						});
					}}
				>
					<SquareAsteriskIcon size={lucideIconMenuSize} />
					{resetPasswordText}
				</DropdownMenuItem>

				{sessionState.get().loggedIn && (
					<DropdownMenuItem
						disabled={!sessionState.get().loggedIn}
						onClick={async () => {
							return Promise.resolve().then(() => {
								closeAccountMenu();
								siteLayoutState.logoutAsync();
							});
						}}
					>
						<LogOutIcon size={lucideIconMenuSize} />
						{siteLayoutState.translate("Logout")}
					</DropdownMenuItem>
				)}
				<DropdownMenuSeparator />
				<DropdownMenuItem disabled className="block cursor-default">
					<div css={css`font-size:.7rem;text-align:center;`}>
						<div>RentalWorks&reg; v{version}</div>
						<div>&copy;{year} Database Works, Inc.</div>
					</div>
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};
